
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { GeneratedContent } from './content-generator/types';
import { CONTENT_TYPES, MOCK_GENERATED } from './content-generator/constants';
import ContentTypeSelector from './content-generator/ContentTypeSelector';
import GeneratedContentList from './content-generator/GeneratedContentList';
import ContentTips from './content-generator/ContentTips';

const ContentGenerator: React.FC = () => {
  const { toast } = useToast();
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState<GeneratedContent[]>(MOCK_GENERATED);
  const [promptInput, setPromptInput] = useState('');
  
  const handleGenerate = () => {
    if (!selectedType) {
      toast({
        title: "Please select a content type",
        description: "Choose what kind of content you want to generate",
        variant: "destructive"
      });
      return;
    }
    
    setIsGenerating(true);
    
    // Simulate API call
    setTimeout(() => {
      const selectedTypeData = CONTENT_TYPES.find(t => t.id === selectedType);
      
      const newContent: GeneratedContent = {
        id: Date.now().toString(),
        type: selectedTypeData?.name || 'Content',
        platform: selectedTypeData?.platforms[0] || 'all',
        content: promptInput 
          ? `AI-generated content based on your prompt: "${promptInput.substring(0, 20)}..."`
          : `Just dropped my new track "Summer Vibes"! This one's been in the works for months and I can't wait for you all to hear it. Real talk - this might be my favorite release yet. 🎵 #newmusic #summervibe #musicproducer`,
        engagement: 70 + Math.floor(Math.random() * 25)
      };
      
      setGeneratedContent(prev => [newContent, ...prev]);
      setIsGenerating(false);
      
      toast({
        title: "Content generated!",
        description: `Your ${selectedTypeData?.name} has been created successfully.`
      });
    }, 1500);
  };
  
  const handleCopy = (content: string) => {
    navigator.clipboard.writeText(content);
    
    toast({
      title: "Copied to clipboard!",
      description: "The content has been copied to your clipboard."
    });
  };

  const handleRegenerate = (contentId: string) => {
    // In a real implementation, this would call the AI to regenerate the content
    toast({
      title: "Regenerating content",
      description: "This feature would regenerate your content with AI"
    });
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-2xl font-bold mb-1">Content Generator</h2>
          <p className="text-muted-foreground">AI-powered social media content for music promotion</p>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <GeneratedContentList 
            contentItems={generatedContent} 
            onCopy={handleCopy} 
            onRegenerate={handleRegenerate}
          />
        </div>
        
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Create New Content</CardTitle>
              <CardDescription>Generate social media content for your music</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <ContentTypeSelector 
                contentTypes={CONTENT_TYPES} 
                selectedType={selectedType} 
                onSelectType={setSelectedType} 
              />
              
              <Separator />
              
              <div>
                <h3 className="text-sm font-medium mb-2">Custom Prompt (Optional)</h3>
                <Textarea 
                  placeholder="Describe what you'd like the AI to generate..." 
                  className="min-h-[100px]"
                  value={promptInput}
                  onChange={(e) => setPromptInput(e.target.value)}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Add details about your music, release, or what you're promoting
                </p>
              </div>
              
              <Button 
                className="w-full"
                onClick={handleGenerate}
                disabled={isGenerating || !selectedType}
              >
                {isGenerating ? "Generating..." : "Generate Content"}
              </Button>
            </CardContent>
          </Card>
          
          <ContentTips />
        </div>
      </div>
    </div>
  );
};

export default ContentGenerator;
